import type {
  DocumentChange,
  DocumentData,
  Unsubscribe,
} from 'firebase/firestore'
import { getFirestoreInstance } from '@/lib/firebase'

// Lazy-loaded Firestore utilities (cached after first load)
let _firestoreUtils: Awaited<ReturnType<typeof loadFirestoreUtils>> | null = null
async function loadFirestoreUtils() {
  const mod = await import('firebase/firestore')
  const firestore = await getFirestoreInstance()
  return { firestore, collection: mod.collection, doc: mod.doc, setDoc: mod.setDoc, deleteDoc: mod.deleteDoc, getDocs: mod.getDocs, onSnapshot: mod.onSnapshot }
}
async function getFs() {
  if (!_firestoreUtils) _firestoreUtils = await loadFirestoreUtils()
  return _firestoreUtils
}
import {
  db,
  getAllTasks,
  getAllCategories,
  getAllTaskGroups,
  getTaskBySyncId,
  getCategoryBySyncId,
  getCompletionLogBySyncId,
  getTaskGroupBySyncId,
  generateSyncId,
  addTask as dbAddTask,
  updateTask as dbUpdateTask,
  deleteTask as dbDeleteTask,
  addCategory as dbAddCategory,
  updateCategory as dbUpdateCategory,
  deleteCategory as dbDeleteCategory,
  addCompletionLog as dbAddCompletionLog,
  deleteCompletionLogBySyncId as dbDeleteCompletionLogBySyncId,
  addTaskGroup as dbAddTaskGroup,
  updateTaskGroup as dbUpdateTaskGroup,
  deleteTaskGroup as dbDeleteTaskGroup,
} from './database'
import type { Task, Category, CompletionLog, TaskGroup } from '@/lib/types'
import { useSettingsStore } from '@/stores/settingsStore'

// ─── Firestore Document Types ───────────────────────

interface FirestoreTask {
  title: string
  categorySyncId: string | null
  status: string
  priority: string
  isFlagged: boolean
  isStarred: boolean
  dueDate?: string
  dueTime?: string
  alarm: unknown
  repeat: unknown
  memo?: string
  subtasks: unknown[]
  completedAt?: string
  createdAt: string
  updatedAt: string
  sortOrder: number
  googleTaskId?: string
  linkedMemos?: import('@/lib/types').LinkedMemoRef[]
  // FCM 알림용 — Cloud Functions 쿼리 최적화
  nextAlarmAt?: string | null  // ISO 8601 or null (merge 시 stale 값 제거)
  alarmSent?: boolean     // Cloud Function이 전송 후 true 설정
}

interface FirestoreCategory {
  name: string
  color: string
  icon?: string
  isDefault: boolean
  sortOrder: number
  createdAt: string
  updatedAt: string
}

interface FirestoreCompletionLog {
  taskSyncId: string
  completedAt: string
  date: string
}

interface FirestoreTaskGroup {
  name: string
  description?: string
  color: string
  icon?: string
  taskSyncIds: string[]
  sortOrder: number
  createdAt: string
  updatedAt: string
}

// ─── Background Sync Queue ──────────────────────────

interface SyncQueueEntry {
  type: 'push-task' | 'push-category' | 'delete-task' | 'delete-category' | 'push-group' | 'delete-group'
  syncId: string
  data?: unknown
  timestamp: string
  retryCount?: number // #6: 재시도 횟수 추적
  userId?: string // #24: 항목을 생성한 사용자 — 다른 계정 세션에서 실행 방지
}

const SYNC_QUEUE_KEY = 'tdl_sync_queue'

function getSyncQueue(): SyncQueueEntry[] {
  try {
    return JSON.parse(localStorage.getItem(SYNC_QUEUE_KEY) || '[]')
  } catch { return [] }
}

const SYNC_QUEUE_MAX_SIZE = 100 // #5: 큐 최대 크기
const SYNC_QUEUE_MAX_RETRIES = 5 // #6: 항목당 최대 재시도 횟수

/** 외부 모듈에서 sync queue에 항목을 추가할 수 있도록 export */
export function addToSyncQueueExternal(entry: Omit<SyncQueueEntry, 'retryCount'>) {
  // #24: 현재 사용자로 태깅하여 다른 계정 세션에서 실행되지 않도록 함
  addToSyncQueue({ ...entry, userId: entry.userId ?? currentUserId ?? undefined, retryCount: 0 } as SyncQueueEntry)
}

// #24: push/delete는 같은 syncId에 대해 서로 상쇄 관계 — 한쪽이 큐에 들어오면
// 반대편의 보류 중인 항목을 제거하여 stale delete가 막 복원된 doc을 지우는 것을 방지.
const OPPOSITE_QUEUE_TYPE: Partial<Record<SyncQueueEntry['type'], SyncQueueEntry['type']>> = {
  'push-task': 'delete-task',
  'delete-task': 'push-task',
  'push-category': 'delete-category',
  'delete-category': 'push-category',
  'push-group': 'delete-group',
  'delete-group': 'push-group',
}

function addToSyncQueue(entry: SyncQueueEntry) {
  const taggedEntry: SyncQueueEntry = { ...entry, userId: entry.userId ?? currentUserId ?? undefined }
  const queue = getSyncQueue()
  // Deduplicate: remove existing entry with same type+syncId, preserving retryCount
  const existing = queue.find(e => e.type === taggedEntry.type && e.syncId === taggedEntry.syncId)
  // #24: 같은 syncId의 반대 타입(상쇄 항목)도 함께 제거 — 예: push-task가 들어오면 보류된 delete-task 폐기
  const opposite = OPPOSITE_QUEUE_TYPE[taggedEntry.type]
  const filtered = queue.filter(e =>
    !(e.type === taggedEntry.type && e.syncId === taggedEntry.syncId) &&
    !(opposite != null && e.type === opposite && e.syncId === taggedEntry.syncId)
  )
  filtered.push({ ...taggedEntry, retryCount: taggedEntry.retryCount ?? existing?.retryCount ?? 0 })
  // #5: FIFO 방식으로 오래된 항목부터 제거
  while (filtered.length > SYNC_QUEUE_MAX_SIZE) filtered.shift()
  try {
    localStorage.setItem(SYNC_QUEUE_KEY, JSON.stringify(filtered))
  } catch {
    // localStorage 용량 초과 시 절반만 유지
    const half = filtered.slice(Math.floor(filtered.length / 2))
    try { localStorage.setItem(SYNC_QUEUE_KEY, JSON.stringify(half)) } catch { console.warn('[SyncQueue] localStorage 용량 초과 — sync queue 저장 실패, 데이터 유실 가능') }
  }
}

/** #24: 로그아웃 시 sync queue 비우기 — 다른 세션/계정으로 stale 항목이 재생되는 것 방지 */
export function clearSyncQueue() {
  localStorage.removeItem(SYNC_QUEUE_KEY)
}

async function processSyncQueue(): Promise<void> {
  if (!currentUserId) return
  const queue = getSyncQueue()
  if (queue.length === 0) return

  const failed: SyncQueueEntry[] = []
  for (const entry of queue) {
    // #24: 다른 계정에서 생성된 항목은 현재 사용자의 컬렉션에 실행되지 않도록 폐기.
    // userId가 없는 레거시 항목은 현재 사용자 것으로 간주하여 그대로 처리.
    if (entry.userId != null && entry.userId !== currentUserId) {
      console.warn(`[SyncQueue] 다른 사용자 항목 폐기: ${entry.type} ${entry.syncId}`)
      continue
    }
    // #6: 최대 재시도 횟수 초과 시 폐기
    if ((entry.retryCount ?? 0) >= SYNC_QUEUE_MAX_RETRIES) {
      console.warn(`[SyncQueue] 최대 재시도 초과, 폐기: ${entry.type} ${entry.syncId}`)
      continue
    }
    try {
      switch (entry.type) {
        case 'push-task': {
          const task = await getTaskBySyncId(entry.syncId)
          if (task) await pushTask(task)
          break
        }
        case 'delete-task':
          // #24: 동일 syncId가 로컬에 복원되어 있으면(undo/다른 기기 재import) stale delete 폐기 —
          // 막 복원된 cloud doc을 지워 데이터 유실되는 것을 방지.
          if (!(await getTaskBySyncId(entry.syncId))) {
            await deleteTaskFromCloud(entry.syncId)
          }
          break
        case 'push-category': {
          const cat = await getCategoryBySyncId(entry.syncId)
          if (cat) await pushCategory(cat)
          break
        }
        case 'delete-category':
          if (!(await getCategoryBySyncId(entry.syncId))) {
            await deleteCategoryFromCloud(entry.syncId)
          }
          break
        case 'push-group': {
          const group = await getTaskGroupBySyncId(entry.syncId)
          if (group) await pushTaskGroup(group)
          break
        }
        case 'delete-group':
          if (!(await getTaskGroupBySyncId(entry.syncId))) {
            await deleteTaskGroupFromCloud(entry.syncId)
          }
          break
      }
    } catch {
      failed.push({ ...entry, retryCount: (entry.retryCount ?? 0) + 1 })
    }
  }

  if (failed.length > 0) {
    localStorage.setItem(SYNC_QUEUE_KEY, JSON.stringify(failed))
  } else {
    clearSyncQueue()
  }
}

// Listen for online event to process queued sync operations
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    processSyncQueue().catch(console.error)
  })

  // Background Sync — SW에서 sync 요청 수신
  navigator.serviceWorker?.addEventListener('message', (event) => {
    if (event.data?.type === 'SYNC_REQUESTED') {
      processSyncQueue().catch(console.error)
    }
  })
}

/** Background Sync 등록 — 오프라인 시 호출하면 연결 복구 시 SW가 sync 이벤트 발생 */
export function registerBackgroundSync(): void {
  if ('serviceWorker' in navigator && 'SyncManager' in window) {
    navigator.serviceWorker.ready.then((reg) => {
      (reg as ServiceWorkerRegistration & { sync: { register: (tag: string) => Promise<void> } })
        .sync.register('sync-queue').catch(() => {})
    })
  }
}

// ─── Module State ───────────────────────────────────

let unsubTasks: Unsubscribe | null = null
let unsubCategories: Unsubscribe | null = null
let unsubLogs: Unsubscribe | null = null
let unsubGroups: Unsubscribe | null = null
let currentUserId: string | null = null
let mergeInProgress = false
const recentlyPushed = new Set<string>()

// C1: 머지 중 수신된 변경을 큐잉하여 데이터 유실 방지
let pendingTaskChanges: DocumentChange<DocumentData>[] = []
let pendingCategoryChanges: DocumentChange<DocumentData>[] = []
let pendingLogChanges: DocumentChange<DocumentData>[] = []
let pendingGroupChanges: DocumentChange<DocumentData>[] = []

function markPushed(syncId: string) {
  recentlyPushed.add(syncId)
  setTimeout(() => recentlyPushed.delete(syncId), 10_000) // #21: 10초로 통일 (Google Tasks 측과 동일)
}

// ─── Debounced Push ────────────────────────────────

const pushTimers = new Map<string, ReturnType<typeof setTimeout>>()

export function debouncedPushTask(task: Task): void {
  if (!task.syncId) {
    pushTask(task).catch(console.error)
    return
  }
  const existing = pushTimers.get(task.syncId)
  if (existing) clearTimeout(existing)
  pushTimers.set(task.syncId, setTimeout(() => {
    pushTimers.delete(task.syncId!)
    pushTask(task).catch(console.error)
  }, 300))
}

// ─── Store Refresh Callbacks ────────────────────────

let refreshTasksFn: (() => Promise<void>) | null = null
let refreshCategoriesFn: (() => Promise<void>) | null = null
let refreshGroupsFn: (() => Promise<void>) | null = null

export function registerRefreshCallbacks(
  refreshTasks: () => Promise<void>,
  refreshCategories: () => Promise<void>,
  refreshGroups?: () => Promise<void>,
) {
  refreshTasksFn = refreshTasks
  refreshCategoriesFn = refreshCategories
  refreshGroupsFn = refreshGroups ?? null
}

// ─── Helpers ────────────────────────────────────────

/** Firestore rejects `undefined` values — strip them before setDoc */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function stripUndefined<T>(obj: T): T {
  return Object.fromEntries(
    Object.entries(obj as any).filter(([, v]) => v !== undefined)
  ) as T
}

// ─── Timestamp Comparison ────────────────────────

/** #10: 타임스탬프 비교 시 2초 허용 — 기기 간 시계 차이 보정 */
const CLOCK_SKEW_TOLERANCE_MS = 2000

function isNewer(a: string, b: string): boolean {
  return new Date(a).getTime() - new Date(b).getTime() > CLOCK_SKEW_TOLERANCE_MS
}

function isEffectivelySame(a: string, b: string): boolean {
  return Math.abs(new Date(a).getTime() - new Date(b).getTime()) <= CLOCK_SKEW_TOLERANCE_MS
}

// ─── Converters ─────────────────────────────────────

// FCM: dueDate + dueTime + alarm 조합으로 알림 시각 계산
function computeNextAlarmAt(task: Task): string | undefined {
  if (!task.alarm?.enabled || !task.dueDate) return undefined
  if (task.status === 'completed') return undefined

  // alarm.time이 명시되어 있으면 그 시각 사용 (로컬→UTC 변환)
  if (task.alarm.time) {
    const dt = new Date(`${task.dueDate}T${task.alarm.time}:00`)
    if (!isNaN(dt.getTime())) return dt.toISOString()
  }

  // minutesBefore + dueTime 조합
  if (task.alarm.minutesBefore != null && task.dueTime) {
    const dueDateTime = new Date(`${task.dueDate}T${task.dueTime}:00`)
    if (!isNaN(dueDateTime.getTime())) {
      const alarmTime = new Date(dueDateTime.getTime() - task.alarm.minutesBefore * 60 * 1000)
      return alarmTime.toISOString()
    }
  }

  // 기본값: 마감일 09:00 기준 (dueTime 없을 때)
  const baseTime = new Date(`${task.dueDate}T09:00:00`)
  if (task.alarm.minutesBefore != null) {
    return new Date(baseTime.getTime() - task.alarm.minutesBefore * 60 * 1000).toISOString()
  }
  return baseTime.toISOString()
}

function taskToFirestore(task: Task, categories: Category[], isNew = false): FirestoreTask {
  const cat = categories.find((c) => c.id === task.categoryId)
  const nextAlarmAt = computeNextAlarmAt(task)
  return stripUndefined<FirestoreTask>({
    title: task.title,
    categorySyncId: cat?.syncId || null,
    status: task.status,
    priority: task.priority,
    isFlagged: task.isFlagged,
    isStarred: task.isStarred,
    dueDate: task.dueDate,
    dueTime: task.dueTime,
    alarm: task.alarm,
    repeat: task.repeat,
    memo: task.memo,
    subtasks: task.subtasks,
    linkedMemos: task.linkedMemos?.filter(Boolean),
    completedAt: task.completedAt,
    createdAt: task.createdAt,
    updatedAt: task.updatedAt,
    sortOrder: task.sortOrder,
    googleTaskId: task.googleTaskId || undefined,
    nextAlarmAt: nextAlarmAt ?? null,  // null: Firestore에서 stale 값 제거 (merge 시)
    // 신규 or 알람 활성+nextAlarmAt 존재 시 alarmSent 리셋 — Cloud Function이 재전송
    ...(isNew || (task.alarm?.enabled && nextAlarmAt) ? { alarmSent: false } : {}),
  })
}

function categoryToFirestore(cat: Category): FirestoreCategory {
  return stripUndefined<FirestoreCategory>({
    name: cat.name,
    color: cat.color,
    icon: cat.icon,
    isDefault: cat.isDefault,
    sortOrder: cat.sortOrder,
    createdAt: cat.createdAt,
    updatedAt: cat.updatedAt || cat.createdAt,
  })
}

// M4: N+1 최적화 — 배치 처리용 Map 빌드
async function buildCategorySyncIdMap(): Promise<Map<string, number>> {
  const allCats = await getAllCategories()
  return new Map(
    allCats.filter(c => c.syncId && c.id != null).map(c => [c.syncId!, c.id!])
  )
}

function resolveCategorySyncIdFast(map: Map<string, number>, syncId: string | null): number | null {
  if (!syncId) return null
  return map.get(syncId) ?? null
}

// ─── Push Operations ────────────────────────────────

export async function pushTask(task: Task, isNew = false): Promise<void> {
  if (!currentUserId || !task.syncId) return
  try {
    const { firestore, doc, setDoc } = await getFs()
    const categories = await getAllCategories()
    const docRef = doc(firestore, `users/${currentUserId}/tasks`, task.syncId)
    const data = taskToFirestore(task, categories, isNew)
    // 신규: 전체 쓰기 (alarmSent: false 포함) / 업데이트: merge (Cloud Function의 alarmSent 보존)
    await setDoc(docRef, data, isNew ? {} : { merge: true })
    // #B21: 실제 쓰기가 커밋된 후에만 echo 억제 — 오프라인 큐잉 시 원격 변경 유실 방지
    markPushed(task.syncId)
  } catch (err) {
    if (!navigator.onLine && task.syncId) {
      addToSyncQueue({ type: 'push-task', syncId: task.syncId, timestamp: new Date().toISOString() })
    } else {
      throw err
    }
  }
}

/** 스누즈: Firestore에서 nextAlarmAt 재설정 + alarmSent 리셋 → Cloud Function이 재전송 */
export async function resetAlarmForSnooze(syncId: string, nextAlarmAt: string): Promise<void> {
  if (!currentUserId) return
  const { firestore, doc } = await getFs()
  const { updateDoc } = await import('firebase/firestore')
  const docRef = doc(firestore, `users/${currentUserId}/tasks`, syncId)
  await updateDoc(docRef, { nextAlarmAt, alarmSent: false })
}

export async function pushCategory(category: Category): Promise<void> {
  if (!currentUserId || !category.syncId) return
  try {
    const { firestore, doc, setDoc } = await getFs()
    const docRef = doc(firestore, `users/${currentUserId}/categories`, category.syncId)
    await setDoc(docRef, categoryToFirestore(category))
    // #B21: 쓰기 커밋 후 echo 억제
    markPushed(category.syncId)
  } catch (err) {
    // #12: 오프라인 시 sync queue에 추가
    if (!navigator.onLine && category.syncId) {
      addToSyncQueue({ type: 'push-category', syncId: category.syncId, timestamp: new Date().toISOString() })
    } else {
      throw err
    }
  }
}

export async function pushCompletionLog(log: CompletionLog, taskSyncId: string): Promise<void> {
  if (!currentUserId || !log.syncId) return
  try {
    const { firestore, doc, setDoc } = await getFs()
    const docRef = doc(firestore, `users/${currentUserId}/completionLogs`, log.syncId)
    await setDoc(docRef, {
      taskSyncId,
      completedAt: log.completedAt,
      date: log.date,
    } satisfies FirestoreCompletionLog)
    // #B21: 쓰기 커밋 후 echo 억제
    markPushed(log.syncId)
  } catch (err) {
    // #12: CompletionLog는 append-only이므로 오프라인 시 조용히 무시
    if (!navigator.onLine) {
      console.warn('[FirestoreSync] 오프라인 — completionLog push 건너뜀')
    } else {
      throw err
    }
  }
}

export async function deleteTaskFromCloud(syncId?: string): Promise<void> {
  if (!currentUserId || !syncId) return
  try {
    const { firestore, doc, deleteDoc } = await getFs()
    await deleteDoc(doc(firestore, `users/${currentUserId}/tasks`, syncId))
    // #B21: 삭제 커밋 후 echo 억제
    markPushed(syncId)
  } catch (err) {
    if (!navigator.onLine) {
      addToSyncQueue({ type: 'delete-task', syncId, timestamp: new Date().toISOString() })
    } else {
      throw err
    }
  }
}

/**
 * #1: 완료 로그를 클라우드에서 삭제 (완료 취소/Undo 시). pushCompletionLog와 대칭.
 * completionLogs는 push 측에서 오프라인 시 조용히 무시하므로(append-only), 삭제도 동일하게
 * 오프라인이면 무시한다. 삭제는 onSnapshot의 'removed' 핸들러를 통해 다른 기기로 전파된다.
 */
export async function deleteCompletionLogFromCloud(syncId?: string): Promise<void> {
  if (!currentUserId || !syncId) return
  try {
    const { firestore, doc, deleteDoc } = await getFs()
    await deleteDoc(doc(firestore, `users/${currentUserId}/completionLogs`, syncId))
    // #B21: 삭제 커밋 후 echo 억제
    markPushed(syncId)
  } catch (err) {
    if (!navigator.onLine) {
      console.warn('[FirestoreSync] 오프라인 — completionLog 삭제 건너뜀')
    } else {
      throw err
    }
  }
}

export async function deleteCategoryFromCloud(syncId?: string): Promise<void> {
  if (!currentUserId || !syncId) return
  try {
    const { firestore, doc, deleteDoc } = await getFs()
    await deleteDoc(doc(firestore, `users/${currentUserId}/categories`, syncId))
    // #B21: 삭제 커밋 후 echo 억제
    markPushed(syncId)
  } catch (err) {
    // #12: 오프라인 시 sync queue에 추가
    if (!navigator.onLine) {
      addToSyncQueue({ type: 'delete-category', syncId, timestamp: new Date().toISOString() })
    } else {
      throw err
    }
  }
}

// ─── TaskGroup Push/Delete ──────────────────────

async function groupToFirestore(group: TaskGroup): Promise<FirestoreTaskGroup> {
  const tasks = await getAllTasks()
  const taskSyncIds = group.taskIds
    .map(id => tasks.find(t => t.id === id)?.syncId)
    .filter((s): s is string => !!s)
  return stripUndefined<FirestoreTaskGroup>({
    name: group.name,
    description: group.description,
    color: group.color,
    icon: group.icon,
    taskSyncIds,
    sortOrder: group.sortOrder,
    createdAt: group.createdAt,
    updatedAt: group.updatedAt,
  })
}

async function resolveTaskSyncIds(taskSyncIds: string[]): Promise<number[]> {
  if (taskSyncIds.length === 0) return []
  const allTasks = await getAllTasks()
  const syncIdMap = new Map<string, number>()
  for (const t of allTasks) {
    if (t.syncId && t.id) syncIdMap.set(t.syncId, t.id)
  }
  return taskSyncIds
    .map(sid => syncIdMap.get(sid))
    .filter((id): id is number => id !== undefined)
}

export async function pushTaskGroup(group: TaskGroup): Promise<void> {
  if (!currentUserId || !group.syncId) return
  try {
    const { firestore, doc, setDoc } = await getFs()
    const docRef = doc(firestore, `users/${currentUserId}/taskGroups`, group.syncId)
    await setDoc(docRef, await groupToFirestore(group))
    // #B21: 쓰기 커밋 후 echo 억제
    markPushed(group.syncId)
  } catch (err) {
    // #12: 오프라인 시 sync queue에 추가
    if (!navigator.onLine && group.syncId) {
      addToSyncQueue({ type: 'push-group', syncId: group.syncId, timestamp: new Date().toISOString() })
    } else {
      throw err
    }
  }
}

export async function deleteTaskGroupFromCloud(syncId?: string): Promise<void> {
  if (!currentUserId || !syncId) return
  try {
    const { firestore, doc, deleteDoc } = await getFs()
    await deleteDoc(doc(firestore, `users/${currentUserId}/taskGroups`, syncId))
    // #B21: 삭제 커밋 후 echo 억제
    markPushed(syncId)
  } catch (err) {
    // #12: 오프라인 시 sync queue에 추가
    if (!navigator.onLine) {
      addToSyncQueue({ type: 'delete-group', syncId, timestamp: new Date().toISOString() })
    } else {
      throw err
    }
  }
}

// ─── Initial Merge ──────────────────────────────────

async function initialMerge(userId: string): Promise<void> {
  mergeInProgress = true
  try {
    const { firestore, collection, getDocs } = await getFs()
    // Fetch cloud and local data in parallel
    const [cloudCatsSnap, cloudTasksSnap, cloudLogsSnap, cloudGroupsSnap, localCategoriesArr, localTasks, localLogs, localGroups] = await Promise.all([
      getDocs(collection(firestore, `users/${userId}/categories`)),
      getDocs(collection(firestore, `users/${userId}/tasks`)),
      getDocs(collection(firestore, `users/${userId}/completionLogs`)),
      getDocs(collection(firestore, `users/${userId}/taskGroups`)),
      getAllCategories(),
      getAllTasks(),
      db.completionLogs.toArray(),
      getAllTaskGroups(),
    ])
    let localCategories = localCategoriesArr

    // ── Detect new device (never synced before) ─────────
    const isNewDevice =
      localCategories.every((c) => !c.syncId) &&
      localTasks.every((t) => !t.syncId) &&
      localLogs.every((l) => !l.syncId)

    const cloudHasData =
      cloudCatsSnap.size > 0 || cloudTasksSnap.size > 0 || cloudLogsSnap.size > 0

    // ── New device + cloud has data: cloud-first import ─
    if (isNewDevice && cloudHasData) {
      // #B19: clear 전에 기존 로컬 카테고리의 id→name 스냅샷 — 살아남는 로컬 작업의
      // categoryId(숫자 FK)를 cloud 재임포트 후 이름 기준으로 재매핑하기 위함
      const oldLocalCatNameById = new Map<number, string>(
        localCategories
          .filter((c) => c.id != null)
          .map((c) => [c.id!, c.name])
      )

      // Delete local seed categories
      await db.categories.clear()

      // Import cloud categories → local
      for (const d of cloudCatsSnap.docs) {
        const cloudCat = d.data() as FirestoreCategory
        await dbAddCategory({
          syncId: d.id,
          name: cloudCat.name,
          color: cloudCat.color,
          icon: cloudCat.icon,
          isDefault: cloudCat.isDefault,
          sortOrder: cloudCat.sortOrder,
          createdAt: cloudCat.createdAt,
          updatedAt: cloudCat.updatedAt,
        })
      }

      // Import cloud tasks → local (M4: 배치 Map 사용)
      const catMap = await buildCategorySyncIdMap()
      for (const d of cloudTasksSnap.docs) {
        const cloudTask = d.data() as FirestoreTask
        const categoryId = resolveCategorySyncIdFast(catMap, cloudTask.categorySyncId)
        await dbAddTask({
          syncId: d.id,
          title: cloudTask.title,
          categoryId,
          status: cloudTask.status as Task['status'],
          priority: cloudTask.priority as Task['priority'],
          isFlagged: cloudTask.isFlagged,
          isStarred: cloudTask.isStarred,
          dueDate: cloudTask.dueDate,
          dueTime: cloudTask.dueTime,
          alarm: cloudTask.alarm as Task['alarm'],
          repeat: cloudTask.repeat as Task['repeat'],
          memo: cloudTask.memo,
          subtasks: cloudTask.subtasks as Task['subtasks'],
          linkedMemos: cloudTask.linkedMemos,
          completedAt: cloudTask.completedAt,
          createdAt: cloudTask.createdAt,
          updatedAt: cloudTask.updatedAt,
          sortOrder: cloudTask.sortOrder,
          googleTaskId: cloudTask.googleTaskId || undefined,
        })
      }

      // Import cloud completion logs → local
      for (const d of cloudLogsSnap.docs) {
        const cloudLog = d.data() as FirestoreCompletionLog
        const task = await getTaskBySyncId(cloudLog.taskSyncId)
        if (task) {
          await dbAddCompletionLog({
            syncId: d.id,
            taskId: task.id!,
            completedAt: cloudLog.completedAt,
            date: cloudLog.date,
          })
        }
      }

      // Import cloud task groups → local
      for (const d of cloudGroupsSnap.docs) {
        const cloudGroup = d.data() as FirestoreTaskGroup
        const taskIds = await resolveTaskSyncIds(cloudGroup.taskSyncIds || [])
        await dbAddTaskGroup({
          syncId: d.id,
          name: cloudGroup.name,
          description: cloudGroup.description,
          color: cloudGroup.color,
          icon: cloudGroup.icon,
          taskIds,
          sortOrder: cloudGroup.sortOrder,
          createdAt: cloudGroup.createdAt,
          updatedAt: cloudGroup.updatedAt,
        })
      }

      // ── 기존 사용자: 온보딩 건너뛰기 ─────────────
      useSettingsStore.getState().setHasCompletedOnboarding(true)

      // ── 로컬 튜토리얼 작업 정리 (위저드 먼저 완료한 경우) ──
      const tutorialTitles = new Set([
        'To-Do List에 오신 것을 환영합니다! 👋',
        '이 작업을 오른쪽으로 스와이프하여 완료해보세요 👉',
        '작업을 탭하여 상세 내용을 확인하세요 📝',
        '새로운 작업을 추가하려면 아래 + 버튼을 누르세요 ➕',
      ])
      const cloudSyncIds = new Set<string>()
      cloudTasksSnap.forEach((d) => cloudSyncIds.add(d.id))
      const localTasksAfterImport = await getAllTasks()
      const deletedTaskIds = new Set<number>()
      for (const t of localTasksAfterImport) {
        if (tutorialTitles.has(t.title) && (!t.syncId || !cloudSyncIds.has(t.syncId))) {
          await dbDeleteTask(t.id!)
          if (t.syncId) await deleteTaskFromCloud(t.syncId)
          if (t.id != null) deletedTaskIds.add(t.id)
        }
      }

      // #B19: db.categories.clear()로 시드 카테고리가 새 id로 재생성되었으므로,
      // 살아남은 로컬 작업(오프라인에서 생성, syncId 없음)의 categoryId FK가
      // 삭제된 카테고리 행을 가리키는 dangling 상태가 됨. 이름 기준으로 재매핑하여
      // 실제 카테고리 행을 가리키거나 명시적 null(미분류)이 되도록 보정.
      const reimportedCats = await getAllCategories()
      const nameToNewLocalId = new Map<string, number>()
      for (const c of reimportedCats) {
        if (c.id != null && !nameToNewLocalId.has(c.name)) {
          nameToNewLocalId.set(c.name, c.id)
        }
      }
      for (const t of localTasksAfterImport) {
        if (t.id == null || deletedTaskIds.has(t.id)) continue
        if (t.categoryId == null) continue
        // 클라우드에서 막 가져온 태스크(syncId 보유)는 categoryId가 이미 올바르게 해석됨 → 재매핑 금지
        if (t.syncId) continue
        const oldName = oldLocalCatNameById.get(t.categoryId)
        if (oldName == null) continue // 구 시드 FK가 아니면 건드리지 않음 (null 강제 금지)
        const newLocalId = nameToNewLocalId.get(oldName) ?? null
        if (newLocalId !== t.categoryId) {
          await dbUpdateTask(t.id, { categoryId: newLocalId })
        }
      }

      // Refresh stores and skip bidirectional merge
      await refreshTasksFn?.()
      await refreshCategoriesFn?.()
      await refreshGroupsFn?.()
      return
    }

    // 1. Merge categories first

    // Deduplicate existing local categories before merge
    const seenSyncIds = new Set<string>()
    const seenNames = new Set<string>()
    const deduped: typeof localCategories = []
    for (const cat of localCategories) {
      if (cat.syncId && seenSyncIds.has(cat.syncId)) {
        await db.categories.delete(cat.id!)
      } else if (!cat.syncId && seenNames.has(cat.name)) {
        await db.categories.delete(cat.id!)
      } else {
        if (cat.syncId) seenSyncIds.add(cat.syncId)
        seenNames.add(cat.name)
        deduped.push(cat)
      }
    }
    localCategories = deduped

    const cloudCatMap = new Map<string, FirestoreCategory & { syncId: string }>()
    cloudCatsSnap.forEach((d) => {
      cloudCatMap.set(d.id, { ...(d.data() as FirestoreCategory), syncId: d.id })
    })

    const localCatBySyncId = new Map(
      localCategories.filter((c) => c.syncId).map((c) => [c.syncId!, c])
    )

    // Cloud → Local (categories)
    for (const [syncId, cloudCat] of cloudCatMap) {
      const localCat = localCatBySyncId.get(syncId)
      if (localCat) {
        const localUpdated = localCat.updatedAt || localCat.createdAt
        if (cloudCat.updatedAt > localUpdated) {
          await dbUpdateCategory(localCat.id!, {
            name: cloudCat.name,
            color: cloudCat.color,
            icon: cloudCat.icon,
            isDefault: cloudCat.isDefault,
            sortOrder: cloudCat.sortOrder,
            updatedAt: cloudCat.updatedAt,
          })
        } else if (localUpdated > cloudCat.updatedAt) {
          await pushCategory(localCat)
        }
      } else {
        // Fallback: match by name for seed categories without syncId
        const localByName = localCategories.find(
          (c) => !c.syncId && c.name === cloudCat.name
        )
        if (localByName) {
          await dbUpdateCategory(localByName.id!, {
            syncId,
            name: cloudCat.name,
            color: cloudCat.color,
            icon: cloudCat.icon,
            isDefault: cloudCat.isDefault,
            sortOrder: cloudCat.sortOrder,
            updatedAt: cloudCat.updatedAt,
          })
          localByName.syncId = syncId
          localCatBySyncId.set(syncId, localByName)
        } else {
          await dbAddCategory({
            syncId,
            name: cloudCat.name,
            color: cloudCat.color,
            icon: cloudCat.icon,
            isDefault: cloudCat.isDefault,
            sortOrder: cloudCat.sortOrder,
            createdAt: cloudCat.createdAt,
            updatedAt: cloudCat.updatedAt,
          })
        }
      }
    }

    // Local-only categories → Cloud
    for (const localCat of localCategories) {
      if (localCat.syncId && !cloudCatMap.has(localCat.syncId)) {
        await pushCategory(localCat)
      } else if (!localCat.syncId) {
        const syncId = generateSyncId()
        await db.categories.update(localCat.id!, { syncId, updatedAt: localCat.createdAt })
        localCat.syncId = syncId
        localCat.updatedAt = localCat.createdAt
        await pushCategory(localCat)
      }
    }

    // Refresh categories to have latest syncIds
    await getAllCategories()

    // 2. Merge tasks
    const cloudTaskMap = new Map<string, FirestoreTask & { syncId: string }>()
    cloudTasksSnap.forEach((d) => {
      cloudTaskMap.set(d.id, { ...(d.data() as FirestoreTask), syncId: d.id })
    })

    // Cloud-side dedup: 같은 (title+dueDate+status) 문서가 여러 개면 1개만 유지
    const cloudContentKeys = new Map<string, string>() // contentKey → 유지할 syncId
    const cloudDuplicates: string[] = []
    for (const [syncId, ct] of cloudTaskMap) {
      const key = `${(ct.title || '').trim().toLowerCase()}|${ct.dueDate || 'no-date'}|${ct.status}`
      const existing = cloudContentKeys.get(key)
      if (existing) {
        const existingTask = cloudTaskMap.get(existing)!
        // googleTaskId 있는 쪽 유지, 없으면 먼저 생성된 쪽 유지
        if (!ct.googleTaskId && existingTask.googleTaskId) {
          cloudDuplicates.push(syncId)
        } else if (ct.googleTaskId && !existingTask.googleTaskId) {
          cloudDuplicates.push(existing)
          cloudContentKeys.set(key, syncId)
        } else if (ct.createdAt < existingTask.createdAt) {
          cloudDuplicates.push(existing)
          cloudContentKeys.set(key, syncId)
        } else {
          cloudDuplicates.push(syncId)
        }
      } else {
        cloudContentKeys.set(key, syncId)
      }
    }
    // 중복 Firestore 문서 백그라운드 삭제 + map에서 제거
    for (const dupSyncId of cloudDuplicates) {
      deleteTaskFromCloud(dupSyncId).catch(console.error)
      cloudTaskMap.delete(dupSyncId)
    }
    if (cloudDuplicates.length > 0) {
      console.log(`[FirestoreSync] initialMerge: cloud 중복 ${cloudDuplicates.length}개 정리`)
    }

    const localTaskBySyncId = new Map(
      localTasks.filter((t) => t.syncId).map((t) => [t.syncId!, t])
    )

    // Cloud → Local (tasks) — M4: 배치 Map 사용
    const catMapMerge = await buildCategorySyncIdMap()
    for (const [syncId, cloudTask] of cloudTaskMap) {
      const localTask = localTaskBySyncId.get(syncId)
      if (localTask) {
        if (cloudTask.updatedAt > localTask.updatedAt) {
          const categoryId = resolveCategorySyncIdFast(catMapMerge, cloudTask.categorySyncId)
          await dbUpdateTask(localTask.id!, {
            title: cloudTask.title,
            categoryId,
            status: cloudTask.status as Task['status'],
            priority: cloudTask.priority as Task['priority'],
            isFlagged: cloudTask.isFlagged,
            isStarred: cloudTask.isStarred,
            dueDate: cloudTask.dueDate,
            dueTime: cloudTask.dueTime,
            alarm: cloudTask.alarm as unknown as Task['alarm'],
            repeat: cloudTask.repeat as unknown as Task['repeat'],
            memo: cloudTask.memo,
            subtasks: cloudTask.subtasks as unknown as Task['subtasks'],
            linkedMemos: cloudTask.linkedMemos,
            completedAt: cloudTask.completedAt,
            sortOrder: cloudTask.sortOrder,
            googleTaskId: cloudTask.googleTaskId || undefined,
            // #26: 클라우드 권위 updatedAt 보존 (stale write-back 방지)
            updatedAt: cloudTask.updatedAt,
          })
        } else if (localTask.updatedAt > cloudTask.updatedAt) {
          await pushTask(localTask)
        }
      } else {
        const categoryId = resolveCategorySyncIdFast(catMapMerge, cloudTask.categorySyncId)
        await dbAddTask({
          syncId,
          title: cloudTask.title,
          categoryId,
          status: cloudTask.status as Task['status'],
          priority: cloudTask.priority as Task['priority'],
          isFlagged: cloudTask.isFlagged,
          isStarred: cloudTask.isStarred,
          dueDate: cloudTask.dueDate,
          dueTime: cloudTask.dueTime,
          alarm: cloudTask.alarm as Task['alarm'],
          repeat: cloudTask.repeat as Task['repeat'],
          memo: cloudTask.memo,
          subtasks: cloudTask.subtasks as Task['subtasks'],
          linkedMemos: cloudTask.linkedMemos,
          completedAt: cloudTask.completedAt,
          createdAt: cloudTask.createdAt,
          updatedAt: cloudTask.updatedAt,
          sortOrder: cloudTask.sortOrder,
          googleTaskId: cloudTask.googleTaskId || undefined,
        })
      }
    }

    // Local-only tasks → Cloud (내용 기반 매칭으로 중복 방지)
    const usedCloudSyncIds = new Set(
      localTasks.filter(t => t.syncId).map(t => t.syncId!)
    )

    for (const localTask of localTasks) {
      if (localTask.syncId && !cloudTaskMap.has(localTask.syncId)) {
        await pushTask(localTask)
      } else if (!localTask.syncId) {
        // 새 syncId 생성 전 cloud에서 (title+dueDate+status) 매칭 시도
        const localKey = `${(localTask.title || '').trim().toLowerCase()}|${localTask.dueDate || 'no-date'}|${localTask.status}`
        let matchedSyncId: string | null = null

        for (const [syncId, cloudTask] of cloudTaskMap) {
          if (usedCloudSyncIds.has(syncId)) continue
          const cloudKey = `${(cloudTask.title || '').trim().toLowerCase()}|${cloudTask.dueDate || 'no-date'}|${cloudTask.status}`
          if (cloudKey === localKey) {
            matchedSyncId = syncId
            break
          }
        }

        if (matchedSyncId) {
          // 기존 cloud 문서에 연결 — 새 Firestore 문서 생성 방지
          await db.tasks.update(localTask.id!, { syncId: matchedSyncId })
          localTask.syncId = matchedSyncId
          usedCloudSyncIds.add(matchedSyncId)
          const cloudTask = cloudTaskMap.get(matchedSyncId)!
          if (localTask.updatedAt > cloudTask.updatedAt) {
            await pushTask(localTask)
          }
        } else {
          const syncId = generateSyncId()
          await db.tasks.update(localTask.id!, { syncId })
          localTask.syncId = syncId
          await pushTask(localTask)
        }
      }
    }

    // 3. Merge completion logs (append-only union)
    const cloudLogSyncIds = new Set<string>()
    const cloudLogs: (FirestoreCompletionLog & { syncId: string })[] = []
    cloudLogsSnap.forEach((d) => {
      cloudLogSyncIds.add(d.id)
      cloudLogs.push({ ...(d.data() as FirestoreCompletionLog), syncId: d.id })
    })

    const localLogSyncIds = new Set(localLogs.filter((l) => l.syncId).map((l) => l.syncId!))

    for (const cloudLog of cloudLogs) {
      if (!localLogSyncIds.has(cloudLog.syncId)) {
        const task = await getTaskBySyncId(cloudLog.taskSyncId)
        if (task) {
          await dbAddCompletionLog({
            syncId: cloudLog.syncId,
            taskId: task.id!,
            completedAt: cloudLog.completedAt,
            date: cloudLog.date,
          })
        }
      }
    }

    for (const localLog of localLogs) {
      if (localLog.syncId && !cloudLogSyncIds.has(localLog.syncId)) {
        const task = await db.tasks.get(localLog.taskId)
        if (task?.syncId) {
          await pushCompletionLog(localLog, task.syncId)
        }
      } else if (!localLog.syncId) {
        const syncId = generateSyncId()
        await db.completionLogs.update(localLog.id!, { syncId })
        localLog.syncId = syncId
        const task = await db.tasks.get(localLog.taskId)
        if (task?.syncId) {
          await pushCompletionLog(localLog, task.syncId)
        }
      }
    }

    // 4. Merge task groups
    const cloudGroupMap = new Map<string, FirestoreTaskGroup & { syncId: string }>()
    cloudGroupsSnap.forEach((d) => {
      cloudGroupMap.set(d.id, { ...(d.data() as FirestoreTaskGroup), syncId: d.id })
    })

    const localGroupBySyncId = new Map(
      localGroups.filter((g) => g.syncId).map((g) => [g.syncId!, g])
    )

    // Cloud → Local (groups)
    for (const [syncId, cloudGroup] of cloudGroupMap) {
      const localGroup = localGroupBySyncId.get(syncId)
      if (localGroup) {
        if (cloudGroup.updatedAt > localGroup.updatedAt) {
          const taskIds = await resolveTaskSyncIds(cloudGroup.taskSyncIds || [])
          await dbUpdateTaskGroup(localGroup.id!, {
            name: cloudGroup.name,
            description: cloudGroup.description,
            color: cloudGroup.color,
            icon: cloudGroup.icon,
            taskIds,
            sortOrder: cloudGroup.sortOrder,
            updatedAt: cloudGroup.updatedAt,
          })
        } else if (localGroup.updatedAt > cloudGroup.updatedAt) {
          await pushTaskGroup(localGroup)
        }
      } else {
        const taskIds = await resolveTaskSyncIds(cloudGroup.taskSyncIds || [])
        await dbAddTaskGroup({
          syncId,
          name: cloudGroup.name,
          description: cloudGroup.description,
          color: cloudGroup.color,
          icon: cloudGroup.icon,
          taskIds,
          sortOrder: cloudGroup.sortOrder,
          createdAt: cloudGroup.createdAt,
          updatedAt: cloudGroup.updatedAt,
        })
      }
    }

    // Local-only groups → Cloud
    for (const localGroup of localGroups) {
      if (localGroup.syncId && !cloudGroupMap.has(localGroup.syncId)) {
        await pushTaskGroup(localGroup)
      } else if (!localGroup.syncId) {
        const syncId = generateSyncId()
        await db.taskGroups.update(localGroup.id!, { syncId })
        localGroup.syncId = syncId
        await pushTaskGroup(localGroup)
      }
    }

    // Refresh stores
    await refreshTasksFn?.()
    await refreshCategoriesFn?.()
    await refreshGroupsFn?.()
  } finally {
    mergeInProgress = false

    // C1: 머지 중 큐잉된 변경분 처리
    if (pendingTaskChanges.length) {
      const queued = pendingTaskChanges.splice(0)
      handleTaskChanges(queued).catch(console.error)
    }
    if (pendingCategoryChanges.length) {
      const queued = pendingCategoryChanges.splice(0)
      handleCategoryChanges(queued).catch(console.error)
    }
    if (pendingLogChanges.length) {
      const queued = pendingLogChanges.splice(0)
      handleLogChanges(queued).catch(console.error)
    }
    if (pendingGroupChanges.length) {
      const queued = pendingGroupChanges.splice(0)
      handleGroupChanges(queued).catch(console.error)
    }
  }
}

// ─── Real-time Listeners ────────────────────────────

// #1: onSnapshot 에러 핸들러 — 리스너가 죽으면 자동 재연결
let _listenerRetryTimer: ReturnType<typeof setTimeout> | null = null
let _listenerRetryCount = 0
const MAX_LISTENER_RETRIES = 3

function handleSnapshotError(collectionName: string, err: unknown) {
  console.error(`[FirestoreSync] ${collectionName} 리스너 에러:`, err)

  // 이미 재시도 예약 중이면 중복 방지
  if (_listenerRetryTimer) return

  if (_listenerRetryCount >= MAX_LISTENER_RETRIES) {
    import('@/stores/toastStore').then(({ useToastStore }) => {
      useToastStore.getState().showToast('실시간 동기화가 중단되었습니다. 앱을 새로고침하세요.', 'error')
    }).catch(() => {})
    return
  }

  _listenerRetryCount++
  const delay = Math.min(1000 * Math.pow(2, _listenerRetryCount - 1), 10000)
  console.info(`[FirestoreSync] ${delay}ms 후 리스너 재시작 시도 (${_listenerRetryCount}/${MAX_LISTENER_RETRIES})`)

  _listenerRetryTimer = setTimeout(async () => {
    _listenerRetryTimer = null
    const userId = currentUserId
    if (!userId) return

    // 기존 리스너만 해제 (currentUserId 유지)
    unsubTasks?.(); unsubCategories?.(); unsubLogs?.(); unsubGroups?.()
    unsubTasks = null; unsubCategories = null; unsubLogs = null; unsubGroups = null

    try {
      await startListeners(userId)
      _listenerRetryCount = 0 // 성공 시 카운터 리셋
      console.info('[FirestoreSync] 리스너 재시작 성공')
    } catch (retryErr) {
      console.error('[FirestoreSync] 리스너 재시작 실패:', retryErr)
      handleSnapshotError('retry', retryErr)
    }
  }, delay)
}

async function startListeners(userId: string): Promise<void> {
  const { firestore, collection, onSnapshot } = await getFs()

  unsubTasks = onSnapshot(
    collection(firestore, `users/${userId}/tasks`),
    (snapshot) => {
      if (mergeInProgress) { pendingTaskChanges.push(...snapshot.docChanges()); return }
      handleTaskChanges(snapshot.docChanges())
    },
    (err) => handleSnapshotError('tasks', err)
  )

  unsubCategories = onSnapshot(
    collection(firestore, `users/${userId}/categories`),
    (snapshot) => {
      if (mergeInProgress) { pendingCategoryChanges.push(...snapshot.docChanges()); return }
      handleCategoryChanges(snapshot.docChanges())
    },
    (err) => handleSnapshotError('categories', err)
  )

  unsubLogs = onSnapshot(
    collection(firestore, `users/${userId}/completionLogs`),
    (snapshot) => {
      if (mergeInProgress) { pendingLogChanges.push(...snapshot.docChanges()); return }
      handleLogChanges(snapshot.docChanges())
    },
    (err) => handleSnapshotError('completionLogs', err)
  )

  unsubGroups = onSnapshot(
    collection(firestore, `users/${userId}/taskGroups`),
    (snapshot) => {
      if (mergeInProgress) { pendingGroupChanges.push(...snapshot.docChanges()); return }
      handleGroupChanges(snapshot.docChanges())
    },
    (err) => handleSnapshotError('taskGroups', err)
  )
}

async function handleTaskChanges(changes: DocumentChange<DocumentData>[]): Promise<void> {
  let needsRefresh = false
  // M4: 배치 Map으로 N+1 쿼리 방지
  const catMap = await buildCategorySyncIdMap()

  for (const change of changes) {
    const syncId = change.doc.id
    if (recentlyPushed.has(syncId)) continue

    const cloudTask = change.doc.data() as FirestoreTask

    if (change.type === 'added' || change.type === 'modified') {
      const localTask = await getTaskBySyncId(syncId)
      if (localTask) {
        const categoryId = resolveCategorySyncIdFast(catMap, cloudTask.categorySyncId)
        const cloudData: Partial<Task> = {
          title: cloudTask.title,
          categoryId,
          status: cloudTask.status as Task['status'],
          priority: cloudTask.priority as Task['priority'],
          isFlagged: cloudTask.isFlagged,
          isStarred: cloudTask.isStarred,
          dueDate: cloudTask.dueDate,
          dueTime: cloudTask.dueTime,
          alarm: cloudTask.alarm as Task['alarm'],
          repeat: cloudTask.repeat as Task['repeat'],
          subtasks: cloudTask.subtasks as Task['subtasks'],
          linkedMemos: cloudTask.linkedMemos,
          memo: cloudTask.memo,
          completedAt: cloudTask.completedAt,
          sortOrder: cloudTask.sortOrder,
        }

        if (isNewer(cloudTask.updatedAt, localTask.updatedAt)) {
          // Cloud is newer — apply all cloud changes.
          // #26: 클라우드의 권위 updatedAt을 그대로 보존하여, 적용 직후 로컬이 인위적으로
          // 더 최신으로 보이는 stale write-back을 방지.
          await dbUpdateTask(localTask.id!, {
            ...cloudData,
            googleTaskId: cloudTask.googleTaskId || undefined,
            updatedAt: cloudTask.updatedAt,
          })
          needsRefresh = true
        } else if (!isEffectivelySame(cloudTask.updatedAt, localTask.updatedAt)) {
          // #25: 로컬이 수신된 클라우드 doc보다 엄격히 최신 — base 없이 detectConflicts를
          // 호출하면 모든 차이가 거짓 충돌로 잡혀 불필요한 충돌 해결 모달이 뜬다.
          // 대신 권위 있는 로컬 편집을 다시 push하여 stale 클라우드 스냅샷을 교정한다
          // (initialMerge의 last-write-wins 동작과 일치).
          await pushTask(localTask)
        }
      } else {
        const categoryId = resolveCategorySyncIdFast(catMap, cloudTask.categorySyncId)
        await dbAddTask({
          syncId,
          title: cloudTask.title,
          categoryId,
          status: cloudTask.status as Task['status'],
          priority: cloudTask.priority as Task['priority'],
          isFlagged: cloudTask.isFlagged,
          isStarred: cloudTask.isStarred,
          dueDate: cloudTask.dueDate,
          dueTime: cloudTask.dueTime,
          alarm: cloudTask.alarm as Task['alarm'],
          repeat: cloudTask.repeat as Task['repeat'],
          memo: cloudTask.memo,
          subtasks: cloudTask.subtasks as Task['subtasks'],
          linkedMemos: cloudTask.linkedMemos,
          completedAt: cloudTask.completedAt,
          createdAt: cloudTask.createdAt,
          updatedAt: cloudTask.updatedAt,
          sortOrder: cloudTask.sortOrder,
          googleTaskId: cloudTask.googleTaskId || undefined,
        })
        needsRefresh = true
      }
    } else if (change.type === 'removed') {
      const localTask = await getTaskBySyncId(syncId)
      if (localTask) {
        await dbDeleteTask(localTask.id!)
        needsRefresh = true
      }
    }
  }

  if (needsRefresh) await refreshTasksFn?.()
}

async function handleCategoryChanges(changes: DocumentChange<DocumentData>[]): Promise<void> {
  let needsRefresh = false

  for (const change of changes) {
    const syncId = change.doc.id
    if (recentlyPushed.has(syncId)) continue

    const cloudCat = change.doc.data() as FirestoreCategory

    if (change.type === 'added' || change.type === 'modified') {
      const localCat = await getCategoryBySyncId(syncId)
      if (localCat) {
        const localUpdated = localCat.updatedAt || localCat.createdAt
        if (cloudCat.updatedAt > localUpdated) {
          await dbUpdateCategory(localCat.id!, {
            name: cloudCat.name,
            color: cloudCat.color,
            icon: cloudCat.icon,
            isDefault: cloudCat.isDefault,
            sortOrder: cloudCat.sortOrder,
            updatedAt: cloudCat.updatedAt,
          })
          needsRefresh = true
        }
      } else {
        await dbAddCategory({
          syncId,
          name: cloudCat.name,
          color: cloudCat.color,
          icon: cloudCat.icon,
          isDefault: cloudCat.isDefault,
          sortOrder: cloudCat.sortOrder,
          createdAt: cloudCat.createdAt,
          updatedAt: cloudCat.updatedAt,
        })
        needsRefresh = true
      }
    } else if (change.type === 'removed') {
      const localCat = await getCategoryBySyncId(syncId)
      if (localCat) {
        await dbDeleteCategory(localCat.id!)
        needsRefresh = true
      }
    }
  }

  if (needsRefresh) await refreshCategoriesFn?.()
}

async function handleLogChanges(changes: DocumentChange<DocumentData>[]): Promise<void> {
  for (const change of changes) {
    const syncId = change.doc.id
    if (recentlyPushed.has(syncId)) continue

    if (change.type === 'added') {
      const existing = await getCompletionLogBySyncId(syncId)
      if (!existing) {
        const cloudLog = change.doc.data() as FirestoreCompletionLog
        const task = await getTaskBySyncId(cloudLog.taskSyncId)
        if (task) {
          await dbAddCompletionLog({
            syncId,
            taskId: task.id!,
            completedAt: cloudLog.completedAt,
            date: cloudLog.date,
          })
        }
      }
    } else if (change.type === 'removed') {
      // #1: 완료 취소/Undo로 클라우드에서 로그가 삭제되면 로컬에서도 제거 —
      // 그렇지 않으면 다른 기기가 orphan 로그를 다시 import하여 streak/통계가 부풀려진다.
      const existing = await getCompletionLogBySyncId(syncId)
      if (existing) {
        await dbDeleteCompletionLogBySyncId(syncId)
        // streak/통계 갱신 (다른 기기에서 삭제된 완료 반영)
        try {
          const { useProfileStore } = await import('@/stores/profileStore')
          await useProfileStore.getState().calculateStreak()
        } catch { /* ignore */ }
      }
    }
  }
}

async function handleGroupChanges(changes: DocumentChange<DocumentData>[]): Promise<void> {
  let needsRefresh = false

  for (const change of changes) {
    const syncId = change.doc.id
    if (recentlyPushed.has(syncId)) continue

    const cloudGroup = change.doc.data() as FirestoreTaskGroup

    if (change.type === 'added' || change.type === 'modified') {
      const localGroup = await getTaskGroupBySyncId(syncId)
      if (localGroup) {
        if (cloudGroup.updatedAt > localGroup.updatedAt) {
          const taskIds = await resolveTaskSyncIds(cloudGroup.taskSyncIds || [])
          await dbUpdateTaskGroup(localGroup.id!, {
            name: cloudGroup.name,
            description: cloudGroup.description,
            color: cloudGroup.color,
            icon: cloudGroup.icon,
            taskIds,
            sortOrder: cloudGroup.sortOrder,
            updatedAt: cloudGroup.updatedAt,
          })
          needsRefresh = true
        }
      } else {
        const taskIds = await resolveTaskSyncIds(cloudGroup.taskSyncIds || [])
        await dbAddTaskGroup({
          syncId,
          name: cloudGroup.name,
          description: cloudGroup.description,
          color: cloudGroup.color,
          icon: cloudGroup.icon,
          taskIds,
          sortOrder: cloudGroup.sortOrder,
          createdAt: cloudGroup.createdAt,
          updatedAt: cloudGroup.updatedAt,
        })
        needsRefresh = true
      }
    } else if (change.type === 'removed') {
      const localGroup = await getTaskGroupBySyncId(syncId)
      if (localGroup) {
        await dbDeleteTaskGroup(localGroup.id!)
        needsRefresh = true
      }
    }
  }

  if (needsRefresh) await refreshGroupsFn?.()
}

// ─── Public API ─────────────────────────────────────

let syncInProgress = false

export async function initSync(userId: string): Promise<void> {
  // 동시 실행 방지
  if (syncInProgress) {
    stopSync()
    await new Promise((r) => setTimeout(r, 200))
  }
  if (currentUserId === userId) return

  syncInProgress = true
  stopSync()
  currentUserId = userId

  try {
    await initialMerge(userId)
    await startListeners(userId)
  } catch (err) {
    console.warn('[Sync] initSync 실패, 500ms 후 재시도:', err)
    // 1회 재시도
    await new Promise((r) => setTimeout(r, 500))
    try {
      if (currentUserId === userId) {
        await initialMerge(userId)
        await startListeners(userId)
      }
    } catch (retryErr) {
      console.error('[Sync] 재시도도 실패:', retryErr)
      throw retryErr
    }
  } finally {
    syncInProgress = false
  }
}

export function stopSync(): void {
  unsubTasks?.()
  unsubCategories?.()
  unsubLogs?.()
  unsubGroups?.()
  unsubTasks = null
  unsubCategories = null
  unsubLogs = null
  unsubGroups = null
  currentUserId = null

  // 리스너 재시도 타이머 정리
  if (_listenerRetryTimer) {
    clearTimeout(_listenerRetryTimer)
    _listenerRetryTimer = null
  }
  _listenerRetryCount = 0

  // 레이스 컨디션 방지: 모든 상태 초기화
  mergeInProgress = false
  recentlyPushed.clear()
  pendingTaskChanges = []
  pendingCategoryChanges = []
  pendingLogChanges = []
  pendingGroupChanges = []
  syncInProgress = false
}

export function isSyncActive(): boolean {
  return currentUserId !== null
}
